import { UserManager, UserManagerSettings } from 'oidc-client';
import { Subject } from 'rxjs';

export type Authentication = {
    readonly authority: string;
    readonly clientId: string;
    readonly redirectUrl: string;
    readonly additionalScopes: string;
    readonly loadUserInfo: string;
    readonly extraQueryParams: Record<string, unknown>;
};

export type ApplicationConfiguration = {
    readonly environmentInfo: string;
    readonly customerInfo: string;
    readonly automaticLogoutMinutes: number;
    readonly apiEndpoints: {
        readonly medicationsManagement: string;
        readonly authorisation: string;
        readonly administration: string;
    };
    readonly authentication: Authentication;
    readonly cookieCheck: {
        readonly url: string;
    };
};

let config: ApplicationConfiguration;
let userManager: UserManager;

type Notification = {
    type: 'display' | 'error' | 'success';
    message: string;
};

export const medicationNotification$ = new Subject<Notification>();

export function displayMessage(message: string) {
    medicationNotification$.next({
        type: 'display',
        message: message
    });
}

export function displayError(message: string) {
    medicationNotification$.next({
        type: 'error',
        message: message
    });
}

export function displaySuccess(message: string) {
    medicationNotification$.next({
        type: 'success',
        message: message
    });
}

export function createUserManagerSettings(appConfig: ApplicationConfiguration): UserManagerSettings {
    const authConfig: Authentication = {
        authority: appConfig.authentication.authority || '',
        clientId: appConfig.authentication.clientId || '',
        redirectUrl: appConfig.authentication.redirectUrl?.toLowerCase() || '',
        additionalScopes: appConfig.authentication.additionalScopes || '',
        loadUserInfo: appConfig.authentication.loadUserInfo === 'false' ? 'false' : 'true',
        extraQueryParams: appConfig.authentication.extraQueryParams || {}
    };

    return {
        authority: authConfig.authority,
        client_id: authConfig.clientId,
        redirect_uri: authConfig.redirectUrl,
        response_type: 'id_token token',
        scope: `openid profile email ${authConfig.additionalScopes}`,
        post_logout_redirect_uri: `${authConfig.redirectUrl}#logged_out`,
        silent_redirect_uri: `${authConfig.redirectUrl}renew.html`,
        automaticSilentRenew: true,
        loadUserInfo: authConfig.loadUserInfo !== 'false',
        extraQueryParams: authConfig.extraQueryParams
    };
}

export function configureMedications(settings: { appConfig: ApplicationConfiguration; userManager: UserManager }) {
    config = settings.appConfig;
    userManager = settings.userManager;
}

export const getConfig = (): ApplicationConfiguration => {
    const globalAppConfig = config;
    if (globalAppConfig) return globalAppConfig;

    throw new Error('No config has been set, please make sure your application calls configureMedications');
};

export const getUserManager = (): UserManager => {
    if (userManager) return userManager;

    throw new Error('User Manager was not created, please make sure your application calls configureMedications');
};
